import { ReactNode, useEffect } from 'react';

import Config from '@config';
import { AuthManager } from '@gasbi/shared/common/utils/http/AuthManager';
import tokenManager from '@gasbi/shared/common/utils/tokenManager';
import { WebStorageStateStore } from 'oidc-client-ts';
import {
  AuthProvider,
  hasAuthParams,
  useAuth as useOIDCAuth,
} from 'react-oidc-context';

import useAuth from '@/hooks/useAuth';

const AuthCheck = () => {
  const auth = useOIDCAuth();
  AuthManager.set(auth);

  useEffect(() => {
    if (!auth.events) return;

    const handleSignedIn = () => {
      console.log('Silent token renewal successful');
      tokenManager.setState(auth.user?.access_token);
    };

    const handleSilentError = () => {
      auth.signinRedirect({ state: window.location.pathname });
    };

    auth.events.addUserSignedIn(handleSignedIn);
    auth.events.addSilentRenewError(handleSilentError);

    return () => {
      auth.events.removeUserSignedIn(handleSignedIn);
      auth.events.removeSilentRenewError(handleSilentError);
    };
  }, [auth]);

  useEffect(() => {
    if (auth.isLoading || auth.activeNavigator) return;

    if (auth.isAuthenticated) {
      tokenManager.setState(auth.user?.access_token);
      return;
    }

    if (!hasAuthParams()) {
      auth.signinRedirect({ state: window.location.pathname });
    }
  }, [
    auth.isLoading,
    auth.isAuthenticated,
    auth.activeNavigator,
    auth.user,
    auth.signinRedirect,
    auth,
  ]);

  if (auth.isLoading || auth.activeNavigator) {
    return null;
  }

  if (auth.error) {
    console.error(auth.error);
    auth.signinRedirect({ state: window.location.pathname });
    return;
  }

  return null;
};

const PrivateRoute = ({ children }: { children: ReactNode }) => {
  const { oidc: auth } = useAuth();

  if (auth.isAuthenticated) {
    return children;
  }

  return null;
};

const Provider = ({ children }: { children: ReactNode }) => {
  return (
    <AuthProvider
      authority={Config.VITE_AUTHORITY}
      client_id={Config.VITE_CLIENT_ID}
      redirect_uri={Config.VITE_REDIRECT_URI}
      post_logout_redirect_uri={Config.VITE_POST_LOGOUT_REDIRECT_URI}
      scope={Config.VITE_SCOPE}
      stateStore={new WebStorageStateStore({ store: window.localStorage })}
      automaticSilentRenew
      loadUserInfo
    >
      <AuthCheck />
      <PrivateRoute>{children}</PrivateRoute>
    </AuthProvider>
  );
};

export default Provider;
